import { useState, useEffect, ReactNode } from "react";
import { getIn, useFormikContext } from "formik";
import Button from "./Button";

const daysOfWeek = [
  "monday",
  "tuesday",
  "wednesday",
  "thursday",
  "friday",
  "saturday",
  "sunday",
];

interface Shift {
  startTime: string;
  endTime: string;
}

interface DayEntry {
  day: string;
  shifts: Shift[];
}

interface SchedulePickerProps {
  fieldName?: string; // default: "openingHours"
}

const SchedulePicker: React.FC<SchedulePickerProps> = ({
  fieldName = "openingHours",
}) => {
  const { values, setFieldValue, errors, touched } = useFormikContext<any>();

  const [selectedDay, setSelectedDay] = useState<string>("monday");
  const [startTime, setStartTime] = useState<string>("");
  const [endTime, setEndTime] = useState<string>("");

  const errorMessageClasses = "text-sm text-red-700";

  // Initialize field if missing
  useEffect(() => {
    if (!values[fieldName]) setFieldValue(fieldName, []);
  }, [values, fieldName, setFieldValue]);

  const addShift = () => {
    if (!startTime || !endTime) return;

    const existingDay: DayEntry = values[fieldName].find(
      (d: DayEntry) => d.day === selectedDay
    );

    if (existingDay) {
      // Add shift to existing day
      const updated = values[fieldName].map((d: DayEntry) =>
        d.day === selectedDay
          ? { ...d, shifts: [...d.shifts, { startTime, endTime }] }
          : d
      );
      setFieldValue(fieldName, updated);
    } else {
      // Create new day entry
      setFieldValue(fieldName, [
        ...values[fieldName],
        { day: selectedDay, shifts: [{ startTime, endTime }] },
      ]);
    }

    setStartTime("");
    setEndTime("");
  };

  const removeShift = (day: string, index: number) => {
    const updated = values[fieldName]
      .map((d: DayEntry) =>
        d.day === day
          ? {
              ...d,
              shifts: d.shifts.filter((_: Shift, i: number) => i !== index),
            }
          : d
      )
      .filter((d: DayEntry) => d.shifts.length > 0);

    setFieldValue(fieldName, updated);
  };

  return (
    <div className="w-full mt-6">
      {/* Day Selector */}
      <select
        className="border rounded p-2 mb-2 w-full"
        value={selectedDay}
        onChange={(e) => setSelectedDay(e.target.value)}
      >
        {daysOfWeek.map((day, index) => (
          <option
            key={`${day}-${index}`}
            value={day}
          >
            {day.charAt(0).toUpperCase() + day.slice(1)}
          </option>
        ))}
      </select>

      {/* Time Inputs */}
      <div className="flex gap-2 mb-2">
        <input
          type="time"
          value={startTime}
          onChange={(e) => setStartTime(e.target.value)}
          className="border rounded p-2 w-full"
        />
        <input
          type="time"
          value={endTime}
          onChange={(e) => setEndTime(e.target.value)}
          className="border rounded p-2 w-full"
        />
      </div>

      <Button
        type="button"
        onClick={addShift}
        className="bg-primary text-white rounded px-3 py-1 mb-4"
      >
        + Ajouter un créneau
      </Button>

      {getIn(errors, fieldName) && getIn(touched, fieldName) && (
        <div className={errorMessageClasses}>
          {getIn(errors, fieldName) as ReactNode}
        </div>
      )}

      {/* Display Existing Shifts */}
      <div>
        {values[fieldName].map((dayEntry: DayEntry, index: number) => (
          <div
            key={`${dayEntry.day}-${index}`}
            className="mb-3"
          >
            <h4 className="font-medium capitalize">{dayEntry.day}</h4>
            <ul className="ml-4">
              {dayEntry.shifts.map((shift, index) => (
                <li
                  key={index}
                  className="flex items-center gap-2"
                >
                  {shift.startTime} – {shift.endTime}
                  <Button
                    type="button"
                    className="text-red-500 text-sm"
                    onClick={() => removeShift(dayEntry.day, index)}
                  >
                    Supprimer
                  </Button>
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>
    </div>
  );
};

export default SchedulePicker;
